import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { HostStripeMessageSchema, stripeBridgeEvent } from "@froppio/host-auth";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { authService } from "@/core/auth/services/authService";
import { navigationState } from "@/core/routing/navigationPolicy";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { stripeConnectionOptions, stripeKeys } from "../hooks/stripeQueries";

export function StripeHostBridge() {
  const { user } = useAuth();
  const { businessId, locationId } = useParams();
  const { business } = useCurrentBusiness();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    const refresh = () => {
      if (authService.kind === "host" && businessId && business?.ownerId === user.id) {
        // Refresh even at Home after a manual cold reopen; errors remain in the query for the settings UI.
        void queryClient.fetchQuery(stripeConnectionOptions(businessId, user.id)).catch(() => {});
      } else void queryClient.invalidateQueries({ queryKey: stripeKeys.all });
    };
    const receive = (event: Event) => {
      if (authService.kind !== "host" || !(event instanceof CustomEvent)) return;
      const parsed = HostStripeMessageSchema.safeParse(event.detail);
      if (!parsed.success) return;
      if (parsed.data.type === "resume") refresh();
      if (parsed.data.type === "return" && parsed.data.result.channel === "native") {
        refresh();
        const result = parsed.data.result;
        // Never let an old browser flow switch the user's selected workspace.
        if (result.businessId === businessId && result.locationId === locationId) {
          navigate(`/b/${businessId}/l/${locationId}/stripe`, {
            state: { ...navigationState("start"), stripeOutcome: result.outcome },
          });
        }
      }
    };
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener(stripeBridgeEvent, receive);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", visible);
    if (authService.kind === "host" && businessId && locationId) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ channel: stripeBridgeEvent, version: 1, type: "ready" }));
    }
    return () => {
      window.removeEventListener(stripeBridgeEvent, receive);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [user?.id, businessId, business?.ownerId, locationId, queryClient, navigate]);
  return null;
}
